import React from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { UserPlus, Car, QrCode, Calculator, Zap, Banknote, ArrowRight } from 'lucide-react';
import mockData from '../data/mock';

const HowItWorksSection = () => {
  const { howItWorks } = mockData;

  const iconMap = {
    UserPlus,
    Car,
    QrCode,
    Calculator,
    Zap,
    Banknote
  };

  return (
    <section id="how-it-works" className="py-20 bg-slate-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="bg-gradient-to-r from-[#2D9CDB] to-slate-900 text-white mb-4">
            Cara Kerja
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            {howItWorks.title}
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-12">
            {howItWorks.subtitle}
          </p>
          
          {/* Flow Illustration */}
          <div className="relative mb-16">
            <div className="relative overflow-hidden rounded-3xl shadow-2xl max-w-4xl mx-auto">
              <img 
                src={howItWorks.flowImage}
                alt="Local Shop and Business Ecosystem"
                className="w-full h-[300px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 text-center">
                  <h3 className="font-bold text-slate-900 mb-2">Ekosistem Merchant Lokal</h3>
                  <p className="text-sm text-slate-600">Platform yang menghubungkan semua elemen dalam satu sistem terintegrasi</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {howItWorks.steps.map((step, index) => {
            const IconComponent = iconMap[step.icon];
            const isEven = index % 2 === 1;
            
            return (
              <div key={index} className="relative">
                <Card className={`border-0 shadow-lg hover:shadow-xl transition-all duration-500 group hover:scale-105 ${isEven ? 'md:mt-8' : ''}`}>
                  <CardContent className="p-8 text-center relative overflow-hidden">
                    {/* Background decoration */}
                    <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-[#2D9CDB]/10 to-slate-900/10 rounded-full -translate-y-10 translate-x-10"></div>
                    
                    {/* Step number */}
                    <div className="absolute top-4 left-4 w-8 h-8 bg-gradient-to-r from-[#2D9CDB] to-blue-200 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg">
                      {step.step}
                    </div>

                    {/* Icon */}
                    <div className="w-16 h-16 bg-gradient-to-br from-[#2D9CDB] to-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 relative z-10 shadow-lg">
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>

                    {/* Content */}
                    <h3 className="text-xl font-bold text-slate-900 mb-4 relative z-10">{step.title}</h3>
                    <p className="text-slate-600 relative z-10">{step.description}</p>

                    {/* Connection arrow for desktop */}
                    {index < howItWorks.steps.length - 1 && (index + 1) % 3 !== 0 && (
                      <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-20">
                        <ArrowRight className="h-6 w-6 text-[#2D9CDB]/60" />
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>

        {/* Call to Action */}
        <div className="mt-20 bg-white rounded-3xl p-8 md:p-12 shadow-lg text-center">
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
            Siap Memulai Kerjasama yang Menguntungkan?
          </h3>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            Bergabunglah dengan ekosistem SalingUntung dan rasakan manfaatnya untuk bisnis Anda.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <div className="text-center">
              <p className="text-sm text-slate-500 mb-2">Untuk Driver</p>
              <button
                onClick={() => window.open('https://mitra.salinguntung.com', '_blank')}
                className="bg-[#2D9CDB] hover:bg-[#2589C7] text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 hover:scale-105 shadow-lg"
              >
                Mulai Dapat Komisi
              </button>
            </div>
            <div className="text-center">
              <p className="text-sm text-slate-500 mb-2">Untuk Merchant</p>
              <button
                onClick={() => window.open('https://merchant.salinguntung.com', '_blank')}
                className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 hover:scale-105 shadow-lg"
              >
                Tingkatkan Omzet
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;